import type { DecoderEventDecision } from "./decoderEvents";
import type { PlayerPlaybackFailure } from "../components/playerVideoTypes";
import {
  planDecoderBuffering,
  planDecoderEnded,
  planDecoderPlayingChange,
  planDecoderProgress,
  planDecoderReady,
} from "./decoderEvents";

type SourceGenerationPort = {
  isActiveSurface: (identity: string) => boolean;
  ready: (
    uri: string | undefined,
    positionSeconds: number,
    initialResumeSeconds: number,
  ) => { seekTarget?: number; continuePausedSession: boolean };
  acceptProgress: (seconds: number) => boolean;
};

export type DecoderCoordinatorContext = {
  surfaceIdentity: string;
  streamUrl?: string;
  streamStartSeconds: number;
  resumeSeconds: number;
  timelineDuration: number;
  sourceGeneration: SourceGenerationPort;
  getPosition: () => number;
  getPaused: () => boolean;
  isSessionStarted: () => boolean;
  reconcileSeekProgress: (absoluteSeconds: number) => number | null;
  shouldReportProgress: (absoluteSeconds: number) => boolean;
  shouldPlayNext: () => boolean;
  applyDecision: (decision: DecoderEventDecision) => void;
  onError: (message: string, failure?: PlayerPlaybackFailure) => void;
};

/** Normalize engine callbacks into absolute-clock coordinator decisions. */
export function createDecoderEventHandlers(context: DecoderCoordinatorContext) {
  const authoritative = () =>
    context.sourceGeneration.isActiveSurface(context.surfaceIdentity);

  return {
    onReady(decoderDurationSeconds: number) {
      const active = authoritative();
      const readyResult = active
        ? context.sourceGeneration.ready(
            context.streamUrl,
            context.streamStartSeconds > 0
              ? context.streamStartSeconds
              : context.getPosition(),
            context.resumeSeconds,
          )
        : { seekTarget: undefined, continuePausedSession: false };
      context.applyDecision(planDecoderReady({
        authoritative: active,
        decoderDurationSeconds,
        streamStartSeconds: context.streamStartSeconds,
        seekTarget: readyResult.seekTarget,
        continuePausedSession: readyResult.continuePausedSession,
        sessionStarted: context.isSessionStarted(),
      }));
    },
    onPlayingChange(playing: boolean) {
      context.applyDecision(planDecoderPlayingChange({
        authoritative: authoritative(),
        playing,
        paused: context.getPaused(),
        sessionStarted: context.isSessionStarted(),
      }));
    },
    onProgress(decoderSeconds: number, decoderBufferedSeconds?: number) {
      if (!authoritative() || !Number.isFinite(decoderSeconds)) return;
      const absoluteSeconds = context.streamStartSeconds + decoderSeconds;
      const sourceAccepted = context.sourceGeneration.acceptProgress(absoluteSeconds);
      const reconciledSeconds = sourceAccepted
        ? context.reconcileSeekProgress(absoluteSeconds)
        : null;
      const accepted = reconciledSeconds !== null;
      context.applyDecision(planDecoderProgress({
        authoritative: true,
        accepted,
        absolutePositionSeconds: reconciledSeconds ?? absoluteSeconds,
        absoluteBufferedSeconds: decoderBufferedSeconds !== undefined
          ? context.streamStartSeconds + decoderBufferedSeconds
          : undefined,
        reportProgress: accepted &&
          context.shouldReportProgress(reconciledSeconds),
      }));
    },
    onBuffering(active: boolean) {
      context.applyDecision(planDecoderBuffering(authoritative(), active));
    },
    onEnded() {
      context.applyDecision(planDecoderEnded({
        authoritative: authoritative(),
        timelineDuration: context.timelineDuration,
        playNext: context.shouldPlayNext(),
      }));
    },
    onError(message: string, failure?: PlayerPlaybackFailure) {
      if (authoritative()) context.onError(message, failure);
    },
  };
}
